import { establishmentService } from '../services/establishmentService';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { TFunction } from 'i18next';
import { Establishment } from '../../types/establishmentType';

export class EstablishmentRepository {
  async loadEstablishments(
    isOnline: boolean,
    onUpdate?: (establishments: Establishment[]) => void,
    onLoadingComplete?: () => void
  ): Promise<Establishment[]> {
    const initialData = (await referenceDataSyncService.getEstablishments()) || [];

    if (initialData?.length > 0 && onUpdate) {
      onUpdate(initialData);
    }

    const shouldFetchOnline = isOnline;

    if (shouldFetchOnline) {
      establishmentService
        .getEstablishments()
        .then((res: any) => {
          if (res?.data && res?.data?.length > 0) {
            const sorted = res?.data?.sort((a: any, b: any) =>
              (a.name || '').localeCompare(b.name || '')
            );
            if (onUpdate) {
              onUpdate(sorted);
            }
          }
        })
        .catch((error) => {
          console.warn('Failed to load establishments online in background', error);
        })
        .finally(() => {
          if (onLoadingComplete) {
            onLoadingComplete();
          }
        });
    } else {
      if (onLoadingComplete) {
        onLoadingComplete();
      }
    }

    return initialData;
  }

  getUniquePositionTypes(
    establishments: Establishment[],
    t?: TFunction
  ): { label: string; value: string; id?: number }[] {
    const map = new Map<string, { label: string; value: string; id?: number }>();
    const lang = typeof window !== 'undefined' ? localStorage.getItem('language') || 'en' : 'en';

    establishments.forEach((item: any) => {
      const estType = item?.establishmentType;
      if (estType) {
        if (typeof estType === 'string') {
          map.set(estType.toLowerCase(), { label: estType, value: estType });
        } else if (estType.key) {
          const label =
            lang === 'es'
              ? estType.nameEs || estType.nameEn || estType.name || estType.key
              : estType.nameEn || estType.nameEs || estType.name || estType.key;
          map.set(String(estType.key).toLowerCase(), { label, value: estType.key, id: estType.id });
        } else if (estType.nameEn || estType.nameEs || estType.name) {
          const label =
            lang === 'es'
              ? estType.nameEs || estType.nameEn || estType.name
              : estType.nameEn || estType.nameEs || estType.name;
          const value = estType.key || estType.nameEn || estType.name;
          map.set(String(value).toLowerCase(), { label, value, id: estType.id });
        }
      } else if (item?.positionType) {
        map.set(String(item.positionType).toLowerCase(), {
          label: item.positionType,
          value: item.positionType,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      (a?.label || '').localeCompare(b?.label || '')
    );
  }

  filterByPositionType(establishments: Establishment[], positionType: string): Establishment[] {
    if (!positionType) return [];
    return establishments.filter((est: any) => {
      const estabTypeKey = est?.establishmentType?.key || est?.establishmentType?.nameEn || est?.establishmentType;
      return estabTypeKey === positionType;
    });
  }
}

export const establishmentRepository = new EstablishmentRepository();
